import { useState, useEffect, useMemo } from "react";
import { Button, Card, Spinner } from "react-bootstrap";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Line, Pie } from "react-chartjs-2";
import {
  eachMonthOfInterval,
  format,
  parseISO,
} from "date-fns";
import {
  checkPendingDonations,
  getDonationsSummary,
  getDonationsSummaryByCode,
  getDonationsSummaryByCountry,
  listData,
} from "../services/api";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import DonationRow from "./donationRow";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const PIE_COLORS = [
  "#0d6efd",
  "#198754",
  "#dc3545",
  "#ffc107",
  "#0dcaf0",
  "#6f42c1",
  "#fd7e14",
  "#20c997",
  "#d63384",
  "#6c757d",
  "#adb5bd",
  "#343a40",
];

export default function Donations() {
  const params = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const [cursor, setCursor] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("completed");
  const [isCheckingPending, setIsCheckingPending] = useState(false);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);

  const defaultDateFrom = useMemo(() => {
    const today = new Date();
    const month = today.getMonth();
    const year = today.getFullYear();
    const fyStartYear = month >= 3 ? year : year - 1;
    return `${fyStartYear}-04-01`;
  }, []);

  const defaultDateTo = useMemo(() => {
    const today = new Date();
    const month = today.getMonth();
    const year = today.getFullYear();
    const fyStartYear = month >= 3 ? year : year - 1;
    return `${fyStartYear + 1}-03-31`;
  }, []);

  const [dateFromInput, setDateFromInput] = useState(defaultDateFrom);
  const [dateToInput, setDateToInput] = useState(defaultDateTo);
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [motivationCodes, setMotivationCodes] = useState([]);
  const [motivationCodeFilter, setMotivationCodeFilter] = useState("");
  const [registerFilter, setRegisterFilter] = useState(null);
  const [summaryTotal, setSummaryTotal] = useState(0);
  const [summaryAmountTotal, setSummaryAmountTotal] = useState(0);
  const [summaryMonthly, setSummaryMonthly] = useState([]);
  const [summaryByCode, setSummaryByCode] = useState([]);
  const [summaryByCountry, setSummaryByCountry] = useState([]);
  const [isExporting, setIsExporting] = useState(false);
  const [donations, setDonations] = useState({
    headers: [
      "Email",
      "País",
      "Amount",
      "Payment Method",
      "Donation Status",
      "Donation Type",
      "Transaction ID",
      "Info",
      "Check updates",
      "Motivation Code",
      "Last Updated",
    ],
    data: [],
    count: state?.count || 0,
  });
  const statusTabs = [
    { key: "completed", label: "Completed" },
    { key: "not_completed", label: "Não completed" },
  ];

  async function loadDonations(
    nextCursor = "",
    filter = statusFilter,
    range = { from: dateFrom, to: dateTo },
    motivationCode = motivationCodeFilter,
    register = registerFilter
  ) {
    setIsLoading(true);
    const lookupTerm = register?.id ? "register_id" : "channel_id";
    const lookupValue = register?.id ? register.id : params.channelId ?? "";
    const response = await listData({
      table: "donations",
      term: lookupTerm,
      value: lookupValue,
      limit: 20,
      cursor: nextCursor,
      status: filter === "completed" ? "Completed" : undefined,
      statusNot: filter === "not_completed" ? "Completed" : undefined,
      dateFrom: range.from,
      dateTo: range.to,
      motivationCode: motivationCode || undefined,
    });
    if (response instanceof Error) {
      console.error(response.message);
      setIsLoading(false);
      setHasMore(false);
      return;
    }
    const { data } = response;
    if (!Array.isArray(data)) {
      setIsLoading(false);
      setHasMore(false);
      return;
    }

    const newItems = data.filter((item) => !item.cursor);

    if (!nextCursor && newItems.length <= 0) {
      setIsLoading(false);
      setDonations((prev) => ({
        ...prev,
        data: [],
        count: 0,
      }));
      setHasMore(false);
      return;
    }

    const newCursor = data.find((item) => item.cursor);
    setCursor(newCursor ? newCursor.cursor : "");

    setDonations((prev) => ({
      ...prev,
      title: params.channelId
        ? `Donations from ${data[0]?.channel.name}`
        : "Donations",
      data: nextCursor ? [...prev.data, ...newItems] : newItems,
      count:
        prev.count <= 0 ? data.find((item) => item.count)?.count : prev.count,
    }));

    setHasMore(!!newCursor);
    setIsLoading(false);
  }

  async function loadSummary(
    range = { from: dateFrom, to: dateTo },
    motivationCode = motivationCodeFilter
  ) {
    setIsSummaryLoading(true);
    try {
      const [summaryResponse, byCodeResponse, byCountryResponse] =
        await Promise.all([
          getDonationsSummary({
            dateFrom: range.from,
            dateTo: range.to,
            channelId: params.channelId ?? "",
            motivationCode: motivationCode || undefined,
          }),
          getDonationsSummaryByCode({
            dateFrom: range.from,
            dateTo: range.to,
            channelId: params.channelId ?? "",
            motivationCode: motivationCode || undefined,
          }),
          getDonationsSummaryByCountry({
            dateFrom: range.from,
            dateTo: range.to,
            channelId: params.channelId ?? "",
            motivationCode: motivationCode || undefined,
          }),
        ]);

      if (summaryResponse instanceof Error) throw summaryResponse;
      setSummaryTotal(summaryResponse.data.totalCompleted || 0);
      setSummaryAmountTotal(summaryResponse.data.totalAmount || 0);
      setSummaryMonthly(summaryResponse.data.monthly || []);

      if (!(byCodeResponse instanceof Error)) {
        setSummaryByCode(
          Array.isArray(byCodeResponse.data) ? byCodeResponse.data : []
        );
      }

      if (!(byCountryResponse instanceof Error)) {
        setSummaryByCountry(
          Array.isArray(byCountryResponse.data) ? byCountryResponse.data : []
        );
      }
    } catch (error) {
      console.error("Error loading donations summary:", error);
      setSummaryTotal(0);
      setSummaryMonthly([]);
      setSummaryByCode([]);
      setSummaryByCountry([]);
    } finally {
      setIsSummaryLoading(false);
    }
  }

  async function handleCheckPending() {
    setIsCheckingPending(true);
    try {
      const result = await checkPendingDonations();
      if (result instanceof Error) {
        throw result;
      }
      setCursor("");
      setHasMore(true);
      setDonations((prev) => ({
        ...prev,
        data: [],
        count: 0,
      }));
      await loadDonations(
        "",
        statusFilter,
        { from: dateFrom, to: dateTo },
        motivationCodeFilter
      );
      await loadSummary({ from: dateFrom, to: dateTo }, motivationCodeFilter);
    } catch (error) {
      console.error("Error checking pending donations:", error);
    } finally {
      setIsCheckingPending(false);
    }
  }

  useEffect(() => {
    setCursor("");
    setHasMore(true);
    setDonations((prev) => ({
      ...prev,
      data: [],
      count: 0,
    }));
    loadDonations(
      "",
      statusFilter,
      { from: dateFrom, to: dateTo },
      motivationCodeFilter,
      registerFilter
    );
    loadSummary({ from: dateFrom, to: dateTo }, motivationCodeFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, dateFrom, dateTo, motivationCodeFilter, registerFilter]);

  useEffect(() => {
    if (!hasMore || isLoading) return;
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop + 100 >=
        document.documentElement.offsetHeight
      ) {
        setIsLoading(true);
        loadDonations(cursor);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
    // eslint-disable-next-line
  }, [cursor, hasMore, isLoading, statusFilter, dateFrom, dateTo, motivationCodeFilter, registerFilter]);

  function applyDateFilter() {
    if (!dateFromInput || !dateToInput) return;
    const start = parseISO(dateFromInput);
    const end = parseISO(dateToInput);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return;
    if (start > end) return;
    setDateFrom(dateFromInput);
    setDateTo(dateToInput);
  }

  function formatCsvValue(value) {
    if (value === null || value === undefined) return "";
    const stringValue = String(value);
    if (/[",\n]/.test(stringValue)) {
      return `"${stringValue.replace(/"/g, '""')}"`;
    }
    return stringValue;
  }

  async function fetchAllDonationsForExport() {
    const collected = [];
    let nextCursor = "";
    let hasNext = true;
    let guard = 0;

    while (hasNext && guard < 200) {
      guard += 1;
      const response = await listData({
        table: "donations",
        term: registerFilter?.id ? "register_id" : "channel_id",
        value: registerFilter?.id ? registerFilter.id : params.channelId ?? "",
        limit: 200,
        cursor: nextCursor,
        status: statusFilter === "completed" ? "Completed" : undefined,
        statusNot: statusFilter === "not_completed" ? "Completed" : undefined,
        dateFrom,
        dateTo,
        motivationCode: motivationCodeFilter || undefined,
      });

      if (response instanceof Error) {
        throw response;
      }

      const { data } = response;
      if (!Array.isArray(data)) {
        throw new Error("Invalid data response");
      }

      const batch = data.filter((item) => !item.cursor);
      collected.push(...batch);

      const cursorItem = data.find((item) => item.cursor);
      if (cursorItem?.cursor && cursorItem.cursor !== nextCursor) {
        nextCursor = cursorItem.cursor;
        hasNext = true;
      } else {
        hasNext = false;
      }
    }

    return collected;
  }

  async function handleExportCsv() {
    setIsExporting(true);
    try {
      const allRows = await fetchAllDonationsForExport();
      if (!allRows.length) return;

      const headers = [
        "Email",
        "País",
        "Amount",
        "Currency",
        "Amount BRL",
        "Payment Method",
        "Donation Status",
        "Donation Type",
        "Transaction ID",
        "Motivation Code",
        "Last Updated",
      ];

      const rows = allRows.map((item) => {
        let info = {};
        try {
          const parsed = JSON.parse(item.info || "{}");
          if (parsed && typeof parsed === "object") info = parsed;
        } catch {
          // keep empty
        }
        const currency = (info.currency || "BRL").toUpperCase();
        return [
          item.email,
          info.countryName || info.country || "Brasil",
          item.amount,
          currency,
          // BRL settlement value from Stripe; PT donations are already BRL.
          info.amountBrl ?? (currency === "BRL" ? item.amount : ""),
          item.payment_method,
          item.donation_status,
          item.donation_type,
          item.transaction_id,
          item.motivation_code_id,
          item.updated_at
            ? format(new Date(item.updated_at), "dd/MM/yyyy HH:mm")
            : "",
        ];
      });

      const csv = [
        headers.map(formatCsvValue).join(","),
        ...rows.map((row) => row.map(formatCsvValue).join(",")),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `donations_${dateFrom}_to_${dateTo}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting donations:", error);
    } finally {
      setIsExporting(false);
    }
  }

  const monthlyLabels = useMemo(() => {
    try {
      const start = parseISO(dateFrom);
      const end = parseISO(dateTo);
      const months = eachMonthOfInterval({ start, end });
      return months.map((month) => format(month, "yyyy-MM"));
    } catch (error) {
      return [];
    }
  }, [dateFrom, dateTo]);

  function formatMonthLabel(monthKey) {
    const [year, month] = monthKey.split("-");
    return `${month}/${year}`;
  }

  const lineChartData = useMemo(() => {
    const map = new Map(
      summaryMonthly.map((item) => [item.month, Number(item.total)])
    );
    const labels = monthlyLabels.map(formatMonthLabel);
    const data = monthlyLabels.map((month) => map.get(month) || 0);
    return {
      labels,
      datasets: [
        {
          label: "Completed",
          data,
          borderColor: "#0d6efd",
          backgroundColor: "rgba(13, 110, 253, 0.15)",
          tension: 0.3,
          fill: true,
        },
      ],
    };
  }, [monthlyLabels, summaryMonthly]);

  const amountChartData = useMemo(() => {
    const map = new Map(
      summaryMonthly.map((item) => [item.month, Number(item.amount)])
    );
    const labels = monthlyLabels.map(formatMonthLabel);
    const data = monthlyLabels.map((month) => map.get(month) || 0);
    return {
      labels,
      datasets: [
        {
          label: "Total Amount",
          data,
          borderColor: "#198754",
          backgroundColor: "rgba(25, 135, 84, 0.15)",
          tension: 0.3,
          fill: true,
        },
      ],
    };
  }, [monthlyLabels, summaryMonthly]);

  const pieCountData = useMemo(() => {
    const labels = summaryByCode.map(
      (item) => item.motivationCode || "Sem código"
    );
    const data = summaryByCode.map((item) => item.total);
    const backgroundColors = labels.map(
      (_, i) => PIE_COLORS[i % PIE_COLORS.length]
    );
    return {
      labels,
      datasets: [{ data, backgroundColor: backgroundColors, borderWidth: 1 }],
    };
  }, [summaryByCode]);

  const pieAmountData = useMemo(() => {
    const labels = summaryByCode.map(
      (item) => item.motivationCode || "Sem código"
    );
    const data = summaryByCode.map((item) => item.amount);
    const backgroundColors = labels.map(
      (_, i) => PIE_COLORS[i % PIE_COLORS.length]
    );
    return {
      labels,
      datasets: [{ data, backgroundColor: backgroundColors, borderWidth: 1 }],
    };
  }, [summaryByCode]);

  const lineChartOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { position: "top" },
        title: { display: true, text: "Completed por mês" },
      },
    }),
    []
  );

  const amountChartOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { position: "top" },
        title: { display: true, text: "Total Amount por mês" },
      },
    }),
    []
  );

  const pieCountryData = useMemo(() => {
    const labels = summaryByCountry.map((item) => item.country || "Brasil");
    const data = summaryByCountry.map((item) => item.total);
    const backgroundColors = labels.map(
      (_, i) => PIE_COLORS[i % PIE_COLORS.length]
    );
    return {
      labels,
      datasets: [{ data, backgroundColor: backgroundColors, borderWidth: 1 }],
    };
  }, [summaryByCountry]);

  const pieCountOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { position: "right" },
        title: {
          display: true,
          text: "Doações por Motivation Code (Contagem)",
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const value = context.parsed;
              const pct =
                total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
              return ` ${value} (${pct}%)`;
            },
          },
        },
      },
    }),
    []
  );

  const pieAmountOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { position: "right" },
        title: {
          display: true,
          text: "Doações por Motivation Code (Valor)",
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const value = context.parsed;
              return ` ${new Intl.NumberFormat("pt-BR", {
                style: "currency",
                currency: "BRL",
              }).format(value)}`;
            },
          },
        },
      },
    }),
    []
  );

  const pieCountryOptions = useMemo(
    () => ({
      responsive: true,
      plugins: {
        legend: { position: "right" },
        title: {
          display: true,
          text: "Doações por País (Contagem)",
        },
        tooltip: {
          callbacks: {
            label: (context) => {
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const value = context.parsed;
              const pct =
                total > 0 ? ((value / total) * 100).toFixed(1) : "0.0";
              return ` ${value} (${pct}%)`;
            },
          },
        },
      },
    }),
    []
  );

  const formattedAmountTotal = useMemo(() => {
    try {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(summaryAmountTotal);
    } catch (error) {
      return summaryAmountTotal.toString();
    }
  }, [summaryAmountTotal]);

  const formattedAverageAmount = useMemo(() => {
    const average =
      summaryTotal > 0 ? summaryAmountTotal / summaryTotal : 0;
    try {
      return new Intl.NumberFormat("pt-BR", {
        style: "currency",
        currency: "BRL",
      }).format(average);
    } catch (error) {
      return average.toString();
    }
  }, [summaryAmountTotal, summaryTotal]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    async function loadMotivationCodes() {
      const response = await listData({
        table: "motivation-codes",
        term: "",
        value: "",
        limit: 0,
        cursor: "",
      });
      if (response instanceof Error) {
        console.error(response.message);
        return;
      }
      const codes = (response.data || []).filter((item) => !item.count);
      const filtered = params.channelId
        ? codes.filter((c) => c.channel_id === params.channelId)
        : codes;
      setMotivationCodes(filtered);
    }
    loadMotivationCodes();
  }, [params.channelId]);

  const donationRows = donations.data.map((item, index) => (
    <DonationRow
      key={item.id || item.transaction_id || index}
      index={index}
      donation={item}
      onFilterByRegister={(donation) =>
        setRegisterFilter({ id: donation.register_id, email: donation.email })
      }
    />
  ));

  return (
    <div className="w-full max-w-[1400px] mx-auto px-6 pb-8">
      <div className="w-full flex items-center justify-between py-6 border-b border-slate-200 mb-6">
        <div className="flex flex-col">
          <span className="text-slate-500 text-xs uppercase tracking-wider">
            Ferramentas
          </span>
          <h1 className="text-slate-900 text-2xl font-bold tracking-tight m-0">
            {donations.title ?? "Donations"}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleCheckPending}
            variant="outline-primary"
            disabled={isCheckingPending}
          >
            {isCheckingPending ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Check Pending"
            )}
          </Button>
          <Button onClick={() => navigate(-1)} variant="outline-secondary">
            Back
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 border-0 shadow-sm">
          <div className="text-slate-500 text-xs uppercase tracking-wider font-semibold">
            Total Completed
          </div>
          <div className="text-3xl font-bold text-slate-900 mt-2 tabular-nums">
            {isSummaryLoading ? <Spinner size="sm" /> : summaryTotal}
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <div className="text-slate-500 text-xs uppercase tracking-wider font-semibold">
            Total Amount
          </div>
          <div className="text-3xl font-bold text-emerald-700 mt-2 tabular-nums">
            {isSummaryLoading ? <Spinner size="sm" /> : formattedAmountTotal}
          </div>
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <div className="text-slate-500 text-xs uppercase tracking-wider font-semibold">
            Valor Médio
          </div>
          <div className="text-3xl font-bold text-slate-700 mt-2 tabular-nums">
            {isSummaryLoading ? <Spinner size="sm" /> : formattedAverageAmount}
          </div>
        </Card>
      </div>

      <Card className="p-4 border-0 shadow-sm mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              Início
            </label>
            <input
              type="date"
              className="form-control"
              value={dateFromInput}
              onChange={(event) => setDateFromInput(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              Fim
            </label>
            <input
              type="date"
              className="form-control"
              value={dateToInput}
              onChange={(event) => setDateToInput(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1 min-w-[240px]">
            <label className="text-xs text-slate-500 font-semibold uppercase tracking-wider">
              Motivation Code
            </label>
            <select
              className="form-select"
              value={motivationCodeFilter}
              onChange={(event) => setMotivationCodeFilter(event.target.value)}
            >
              <option value="">Todos</option>
              {motivationCodes.map((code) => (
                <option
                  key={code.motivation_code}
                  value={code.motivation_code}
                >
                  {code.motivation_code}
                  {code.description ? ` — ${code.description}` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="primary" onClick={applyDateFilter}>
              Filtrar
            </Button>
            <Button
              variant="outline-primary"
              onClick={handleExportCsv}
              disabled={!donations.data.length || isExporting}
            >
              {isExporting ? <Spinner size="sm" /> : "Exportar CSV"}
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card className="p-4 border-0 shadow-sm">
          <Line data={lineChartData} options={lineChartOptions} />
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <Line data={amountChartData} options={amountChartOptions} />
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 border-0 shadow-sm">
          {isSummaryLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : summaryByCode.length > 0 ? (
            <Pie data={pieCountData} options={pieCountOptions} />
          ) : (
            <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
              Sem dados para exibir
            </div>
          )}
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          {isSummaryLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : summaryByCode.length > 0 ? (
            <Pie data={pieAmountData} options={pieAmountOptions} />
          ) : (
            <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
              Sem dados para exibir
            </div>
          )}
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          {isSummaryLoading ? (
            <div className="flex items-center justify-center py-16">
              <Spinner />
            </div>
          ) : summaryByCountry.length > 0 ? (
            <Pie data={pieCountryData} options={pieCountryOptions} />
          ) : (
            <div className="flex items-center justify-center py-16 text-slate-500 text-sm">
              Sem dados para exibir
            </div>
          )}
        </Card>
      </div>

      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
          <div className="inline-flex rounded-lg bg-white border border-slate-200 p-1">
            {statusTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() => setStatusFilter(tab.key)}
                className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
                  statusFilter === tab.key
                    ? "bg-blue-600 text-white shadow-sm"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            {registerFilter && (
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800 text-sm">
                <span>
                  Doador:{" "}
                  <strong>{registerFilter.email || registerFilter.id}</strong>
                </span>
                <button
                  type="button"
                  aria-label="Limpar filtro de doador"
                  className="font-bold text-blue-900 hover:text-blue-700 leading-none"
                  onClick={() => setRegisterFilter(null)}
                >
                  ×
                </button>
              </div>
            )}
            <span className="text-sm text-slate-500 tabular-nums">
              {donations.data.length}/{donations.count || 0} registros
            </span>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider w-12">
                  #
                </th>
                {donations.headers.map((header) => (
                  <th
                    key={header}
                    className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>{donationRows}</tbody>
          </table>
        </div>
        {isLoading && (
          <div className="w-full text-center py-6">
            <Spinner />
          </div>
        )}
        {!hasMore && !isLoading && donations.data.length > 0 && (
          <div className="w-full text-center py-3 text-sm text-slate-500 border-t border-slate-100">
            Fim dos registros
          </div>
        )}
        {donations.data.length <= 0 && !isLoading && (
          <div className="w-full text-center py-10 text-slate-500">
            Nenhuma doação encontrada.
          </div>
        )}
      </Card>
    </div>
  );
}
